import { useTabLeadership } from "$global-providers/tab-leadership/provider";
import { isDevEnvironment } from "$routes/_lib/environment";
import { ApolloError } from "@apollo/client";
import { captureException, captureMessage } from "@sentry/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  addRxPlugin,
  createRxDatabase,
  type RxCollection,
  type RxDatabase,
  RxError,
  type RxStorage,
  removeRxDatabase,
} from "rxdb";
import { RxDBCleanupPlugin } from "rxdb/plugins/cleanup";
import { wrappedKeyEncryptionCryptoJsStorage } from "rxdb/plugins/encryption-crypto-js";
import { RxDBLeaderElectionPlugin } from "rxdb/plugins/leader-election";
import { RxDBMigrationPlugin } from "rxdb/plugins/migration-schema";
import { getRxStorageDexie } from "rxdb/plugins/storage-dexie";
import { Provider } from "rxdb-hooks";
import { useDeviceSecretSource } from "./_lib/device-secret-source";
import { hasUnsyncedLocalData } from "./_lib/unsynced-local-data";
import {
  EPOCUS_BILLING_SCHEMA,
  type EpocusBilling,
  RXDB_EPOCUS_BILLING_COLLECTION_NAME,
} from "./collections/epocus-billing";
import {
  EPOCUS_BILLING_STATUS_SCHEMA,
  type EpocusBillingStatus,
  RXDB_EPOCUS_BILLING_STATUS_COLLECTION_NAME,
} from "./collections/epocus-billing-status";
import {
  EPOCUS_CONSENT_DOCUMENT_SCHEMA,
  type EpocusConsentDocument,
  RXDB_EPOCUS_CONSENT_DOCUMENT_COLLECTION_NAME,
} from "./collections/epocus-consent-document";
import {
  EPOCUS_ENCOUNTER_SCHEMA,
  type EpocusEncounter,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
} from "./collections/epocus-encounter";
import {
  EPOCUS_ENCOUNTER_PATIENT_SCHEMA,
  type EpocusEncounterPatient,
  RXDB_EPOCUS_ENCOUNTER_PATIENT_COLLECTION_NAME,
} from "./collections/epocus-encounter-patient";
import {
  EPOCUS_EXAM_SCHEMA,
  type EpocusExam,
  RXDB_EPOCUS_EXAM_COLLECTION_NAME,
} from "./collections/epocus-exam";
import {
  EPOCUS_PATIENT_CONSENT_SCHEMA,
  type EpocusPatientConsent,
  RXDB_EPOCUS_PATIENT_CONSENT_COLLECTION_NAME,
} from "./collections/epocus-patient-consent";
import {
  EPOCUS_PROBE_ASSOCIATION_SCHEMA,
  type EpocusProbeAssociation,
  RXDB_EPOCUS_PROBE_ASSOCIATION_COLLECTION_NAME,
} from "./collections/epocus-probe-association";
import {
  EPOCUS_PROGRAM_SCHEMA,
  type EpocusProgram,
  RXDB_EPOCUS_PROGRAM_COLLECTION_NAME,
} from "./collections/epocus-program";
import {
  EPOCUS_REFERRAL_SCHEMA,
  type EpocusReferral,
  RXDB_EPOCUS_REFERRAL_COLLECTION_NAME,
} from "./collections/epocus-referral";
import {
  EPOCUS_REFERRAL_OUTCOME_SCHEMA,
  type EpocusReferralOutcome,
  RXDB_EPOCUS_REFERRAL_OUTCOME_COLLECTION_NAME,
} from "./collections/epocus-referral-outcome";
import {
  EPOCUS_REVIEW_SCHEMA,
  type EpocusReview,
  RXDB_EPOCUS_REVIEW_COLLECTION_NAME,
} from "./collections/epocus-review";
import {
  IMAGING_INSTANCE_SCHEMA,
  type ImagingInstance,
  RXDB_IMAGING_INSTANCE_COLLECTION_NAME,
} from "./collections/imaging-instance";
import {
  IMAGING_STUDY_SCHEMA,
  type ImagingStudy,
  RXDB_IMAGING_STUDY_COLLECTION_NAME,
} from "./collections/imaging-study";
import {
  RXDB_SYNC_CHECKPOINT_COLLECTION_NAME,
  SYNC_CHECKPOINT_SCHEMA,
  type SyncCheckpoint,
} from "./collections/sync-checkpoint";
import { useRxdbReplication } from "./hooks/use-rxdb-replication";
import { EPOCUS_BILLING_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-billing";
import { EPOCUS_BILLING_STATUS_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-billing-status";
import { EPOCUS_CONSENT_DOCUMENT_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-consent-document";
import { EPOCUS_ENCOUNTER_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-encounter";
import { EPOCUS_ENCOUNTER_PATIENT_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-encounter-patient";
import { EPOCUS_EXAM_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-exam";
import { EPOCUS_PATIENT_CONSENT_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-patient-consent";
import { EPOCUS_PROBE_ASSOCIATION_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-probe-association";
import { EPOCUS_PROGRAM_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-program";
import { EPOCUS_REFERRAL_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-referral";
import { EPOCUS_REFERRAL_OUTCOME_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-referral-outcome";
import { EPOCUS_REVIEW_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-review";
import { IMAGING_INSTANCE_MIGRATION_STRATEGIES } from "./migration-strategies/imaging-instance";
import { IMAGING_STUDY_MIGRATION_STRATEGIES } from "./migration-strategies/imaging-study";
import { SYNC_CHECKPOINT_MIGRATION_STRATEGIES } from "./migration-strategies/sync-checkpoint";
import type { CollectionReplicationState } from "./replication/use-gql-replication";
export const DATABASE_NAME = "elephant";
// The stopgap password shipped before `epocusDeviceSecret` existed. Kept solely
// so a device that already created its database under it is opened rather than
// stranded — see `initDatabase`. Nothing creates a new database with it.
export const TEMPORARY_EPOCUS_RXDB_PASSWORD = "epocus-temporary-rxdb-password";
export interface RxDBCollections {
  [RXDB_EPOCUS_BILLING_COLLECTION_NAME]: RxCollection<EpocusBilling>;
  [RXDB_EPOCUS_BILLING_STATUS_COLLECTION_NAME]: RxCollection<EpocusBillingStatus>;
  [RXDB_EPOCUS_CONSENT_DOCUMENT_COLLECTION_NAME]: RxCollection<EpocusConsentDocument>;
  [RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME]: RxCollection<EpocusEncounter>;
  [RXDB_EPOCUS_ENCOUNTER_PATIENT_COLLECTION_NAME]: RxCollection<EpocusEncounterPatient>;
  [RXDB_EPOCUS_EXAM_COLLECTION_NAME]: RxCollection<EpocusExam>;
  [RXDB_EPOCUS_PATIENT_CONSENT_COLLECTION_NAME]: RxCollection<EpocusPatientConsent>;
  [RXDB_EPOCUS_PROBE_ASSOCIATION_COLLECTION_NAME]: RxCollection<EpocusProbeAssociation>;
  [RXDB_EPOCUS_PROGRAM_COLLECTION_NAME]: RxCollection<EpocusProgram>;
  [RXDB_EPOCUS_REFERRAL_COLLECTION_NAME]: RxCollection<EpocusReferral>;
  [RXDB_EPOCUS_REFERRAL_OUTCOME_COLLECTION_NAME]: RxCollection<EpocusReferralOutcome>;
  [RXDB_EPOCUS_REVIEW_COLLECTION_NAME]: RxCollection<EpocusReview>;
  [RXDB_IMAGING_STUDY_COLLECTION_NAME]: RxCollection<ImagingStudy>;
  [RXDB_IMAGING_INSTANCE_COLLECTION_NAME]: RxCollection<ImagingInstance>;
  [RXDB_SYNC_CHECKPOINT_COLLECTION_NAME]: RxCollection<SyncCheckpoint>;
}

/**
 * Removing a collection from here also narrows the `collectionName` enum on
 * `sync-checkpoint`, so that collection's schema version has to be bumped in
 * the same change — without it every existing device throws RxError DB6 on
 * open. Re-adding a name that has been used before must likewise start above
 * the version it was removed at.
 */
export const RXDB_COLLECTIONS = {
  [RXDB_SYNC_CHECKPOINT_COLLECTION_NAME]: {
    schema: SYNC_CHECKPOINT_SCHEMA,
    migrationStrategies: SYNC_CHECKPOINT_MIGRATION_STRATEGIES,
  },
  [RXDB_IMAGING_STUDY_COLLECTION_NAME]: {
    schema: IMAGING_STUDY_SCHEMA,
    migrationStrategies: IMAGING_STUDY_MIGRATION_STRATEGIES,
  },
  [RXDB_IMAGING_INSTANCE_COLLECTION_NAME]: {
    schema: IMAGING_INSTANCE_SCHEMA,
    migrationStrategies: IMAGING_INSTANCE_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_BILLING_COLLECTION_NAME]: {
    schema: EPOCUS_BILLING_SCHEMA,
    migrationStrategies: EPOCUS_BILLING_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_BILLING_STATUS_COLLECTION_NAME]: {
    schema: EPOCUS_BILLING_STATUS_SCHEMA,
    migrationStrategies: EPOCUS_BILLING_STATUS_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_CONSENT_DOCUMENT_COLLECTION_NAME]: {
    schema: EPOCUS_CONSENT_DOCUMENT_SCHEMA,
    migrationStrategies: EPOCUS_CONSENT_DOCUMENT_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME]: {
    schema: EPOCUS_ENCOUNTER_SCHEMA,
    migrationStrategies: EPOCUS_ENCOUNTER_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_ENCOUNTER_PATIENT_COLLECTION_NAME]: {
    schema: EPOCUS_ENCOUNTER_PATIENT_SCHEMA,
    migrationStrategies: EPOCUS_ENCOUNTER_PATIENT_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_EXAM_COLLECTION_NAME]: {
    schema: EPOCUS_EXAM_SCHEMA,
    migrationStrategies: EPOCUS_EXAM_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_PATIENT_CONSENT_COLLECTION_NAME]: {
    schema: EPOCUS_PATIENT_CONSENT_SCHEMA,
    migrationStrategies: EPOCUS_PATIENT_CONSENT_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_PROBE_ASSOCIATION_COLLECTION_NAME]: {
    schema: EPOCUS_PROBE_ASSOCIATION_SCHEMA,
    migrationStrategies: EPOCUS_PROBE_ASSOCIATION_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_PROGRAM_COLLECTION_NAME]: {
    schema: EPOCUS_PROGRAM_SCHEMA,
    migrationStrategies: EPOCUS_PROGRAM_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_REFERRAL_COLLECTION_NAME]: {
    schema: EPOCUS_REFERRAL_SCHEMA,
    migrationStrategies: EPOCUS_REFERRAL_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_REFERRAL_OUTCOME_COLLECTION_NAME]: {
    schema: EPOCUS_REFERRAL_OUTCOME_SCHEMA,
    migrationStrategies: EPOCUS_REFERRAL_OUTCOME_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_REVIEW_COLLECTION_NAME]: {
    schema: EPOCUS_REVIEW_SCHEMA,
    migrationStrategies: EPOCUS_REVIEW_MIGRATION_STRATEGIES,
  },
} satisfies Parameters<RxDatabase["addCollections"]>[0];

type RxCollectionName =
  | "epocus-billing"
  | "epocus-billing-status"
  | "epocusConsentDocument"
  | "epocusEncounter"
  | "epocusExam"
  | "epocusPatientConsent"
  | "epocusProbeAssociation"
  | "epocus-program"
  | "epocus-referral"
  | "epocus-referral-outcome"
  | "epocusReview"
  | "imagingStudy"
  | "imagingInstance";

export type ReplicationState = {
  [K in RxCollectionName]: CollectionReplicationState;
} & {
  hasUnsyncedChanges: boolean;
};

// biome-ignore lint/suspicious/noExplicitAny: same type as lib
type Storage = RxStorage<any, any>;

// Plugins
addRxPlugin(RxDBMigrationPlugin);

/**
 * This plugin is not required by us, but is internally required by
 * RxDB when destroying the database. If not added here, RxDB will throw an
 * error when destroying the database and generate unnecessary noise in
 * our FE observability systems.
 */
addRxPlugin(RxDBLeaderElectionPlugin);

addRxPlugin(RxDBCleanupPlugin);

const getRxStorage = async (devToolsEnabled: boolean) => {
  console.debug("RxDB: adding encryption");
  let rxStorage = wrappedKeyEncryptionCryptoJsStorage({
    storage: getRxStorageDexie(),
  });

  if (devToolsEnabled) {
    console.debug("RxDB: adding schema validation");
    const { disableWarnings, RxDBDevModePlugin } = await import(
      "rxdb/plugins/dev-mode"
    );
    const { wrappedValidateAjvStorage } = await import("./validation");

    disableWarnings();
    addRxPlugin(RxDBDevModePlugin);

    rxStorage = wrappedValidateAjvStorage({ storage: rxStorage });
  }

  return rxStorage;
};

export enum RxDBInitialisationError {
  RECOVERABLE = "RECOVERABLE",
  FATAL = "FATAL",
  SECRET_UNAVAILABLE = "SECRET_UNAVAILABLE",
  SECRET_REFUSED = "SECRET_REFUSED",
  UNSYNCED_LOCAL_DATA = "UNSYNCED_LOCAL_DATA",
}

const SECRET_ERRORS = new Set([
  RxDBInitialisationError.SECRET_UNAVAILABLE,
  RxDBInitialisationError.SECRET_REFUSED,
]);

const isServerRefusal = (error: Error): boolean =>
  error instanceof ApolloError && error.graphQLErrors.length > 0;
export const RxDBContext = createContext<{
  replicationState: ReplicationState;
  db?: RxDatabase<RxDBCollections>;
  initialisationError?: RxDBInitialisationError;
  resetLocalDatabase: () => Promise<void>;
  canResetLocalDatabase: boolean;
} | null>(null);

export const RxDBProvider: FCwC<{
  dbOverride?: RxDatabase<RxDBCollections>;
}> = ({ children, dbOverride }) => {
  const { isLeader } = useTabLeadership();
  const {
    value: deviceSecretValue,
    loading: deviceSecretLoading,
    error: deviceSecretError,
  } = useDeviceSecretSource();
  const [db, setDb] = useState<RxDatabase<RxDBCollections> | undefined>(
    () => dbOverride || undefined,
  );
  const initialising = useRef(false);
  const [initialisationError, setInitialisationError] = useState<
    RxDBInitialisationError | undefined
  >(undefined);
  const replicationState = useRxdbReplication(
    db as RxDatabase<RxDBCollections>,
  );

  useEffect(() => {
    // Earlier builds cached the device secret under this key.
    localStorage.removeItem("epocusDeviceSecretCache");
  }, []);

  const initDatabase = useCallback(
    async (password: string, { discardUnsyncedData = false } = {}) => {
      if (initialising.current) return;
      initialising.current = true;
      const getStorage = () => getRxStorage(isDevEnvironment());

      const createDb = async (storage: Storage, withPassword: string) => {
        const database = await createRxDatabase<RxDBCollections>({
          name: DATABASE_NAME,
          storage,
          password: withPassword,
          multiInstance: false,
        });
        try {
          await database.addCollections<RxDBCollections>(RXDB_COLLECTIONS);
        } catch (error) {
          // A wrong password surfaces here, not from createRxDatabase, so the
          // database is already live and holds the Dexie handle open. Without
          // this, every failed attempt leaks one and keeps the name registered.
          await database.destroy();
          throw error;
        }
        setDb(database);
        setInitialisationError(undefined);
      };
      try {
        const initialStorage = await getStorage();
        try {
          await createDb(initialStorage, password);
        } catch (error) {
          // A device that created its database under the stopgap password cannot
          // open it with the real one, and RxDB reports that as DB1. Re-keying in
          // place is not something RxDB supports, so the database is opened under
          // the password it was actually created with, and the device keeps its
          // data rather than being recreated.
          if (!isWrongPasswordError(error)) throw error;

          console.warn("Opening the local database with the stopgap password");
          captureMessage(
            "epocus: local database still on the stopgap password",
          );
          try {
            await createDb(initialStorage, TEMPORARY_EPOCUS_RXDB_PASSWORD);
          } catch (stopgapError) {
            if (!isWrongPasswordError(stopgapError)) throw stopgapError;

            if (
              !discardUnsyncedData &&
              (await hasUnsyncedLocalData(DATABASE_NAME))
            ) {
              captureMessage(
                "epocus: local database locked by a superseded device secret, holding unsynced data",
              );
              setInitialisationError(
                RxDBInitialisationError.UNSYNCED_LOCAL_DATA,
              );
              return;
            }

            await recreateDatabase(initialStorage, password, createDb);
          }
        }
      } catch (error) {
        console.error("Error creating database", error);
        captureException(error);
        setInitialisationError(getInitialisationError(error));
      } finally {
        initialising.current = false;
      }
    },
    [],
  );

  const canResetLocalDatabase = !!deviceSecretValue;

  const resetLocalDatabase = useCallback(async () => {
    if (!deviceSecretValue) return;
    await initDatabase(deviceSecretValue, { discardUnsyncedData: true });
  }, [deviceSecretValue, initDatabase]);

  useEffect(() => {
    if (!deviceSecretError || db) {
      return;
    }
    if (initialisationError && !SECRET_ERRORS.has(initialisationError)) {
      return;
    }
    const category = isServerRefusal(deviceSecretError)
      ? RxDBInitialisationError.SECRET_REFUSED
      : RxDBInitialisationError.SECRET_UNAVAILABLE;
    if (category === initialisationError) {
      return;
    }

    console.error("Could not resolve the device secret", deviceSecretError);
    captureException(deviceSecretError);
    setInitialisationError(category);
  }, [deviceSecretError, initialisationError, db]);

  useEffect(() => {
    if (
      deviceSecretValue &&
      initialisationError &&
      SECRET_ERRORS.has(initialisationError)
    ) {
      setInitialisationError(undefined);
    }
  }, [deviceSecretValue, initialisationError]);

  useEffect(() => {
    if (
      db ||
      !isLeader ||
      deviceSecretLoading ||
      !deviceSecretValue ||
      initialisationError
    ) {
      return;
    }

    initDatabase(deviceSecretValue).catch((error) => {
      console.error("Error initializing database", error);
      captureException(error);
      initialising.current = false;
    });
  }, [
    db,
    isLeader,
    initDatabase,
    deviceSecretLoading,
    deviceSecretValue,
    initialisationError,
  ]);

  const rxContextValue = useMemo(
    () => ({
      replicationState,
      db,
      initialisationError,
      resetLocalDatabase,
      canResetLocalDatabase,
    }),
    [
      replicationState,
      db,
      initialisationError,
      resetLocalDatabase,
      canResetLocalDatabase,
    ],
  );

  return (
    <RxDBContext.Provider value={rxContextValue}>
      <Provider db={db}>{children}</Provider>
    </RxDBContext.Provider>
  );
};

export const useRxDBContext = () => {
  const context = useContext(RxDBContext);
  if (!context) {
    throw new Error("useRxDBContext must be used within a RxDBContextProvider");
  }
  return context;
};

// The values of this enum are the error codes thrown by RxDB that we care about
const RxErrorKeys: Record<string, RxError["code"]> = {
  WRONG_PASSWORD: "DB1", // https://rxdb.info/errors.html#DB1
  DB_ALREADY_EXISTS: "DB8", // https://rxdb.info/errors.html#DB8
};

const recreateDatabase = async (
  storage: Storage,
  password: string,
  createDb: (storage: Storage, withPassword: string) => Promise<void>,
) => {
  console.warn(
    "Recreating a local database keyed with a superseded device secret",
  );
  captureMessage(
    "epocus: recreating a local database keyed with a superseded device secret",
  );
  await removeRxDatabase(DATABASE_NAME, storage, password);
  await createDb(storage, password);
};

const isWrongPasswordError = (error: unknown): boolean =>
  error instanceof RxError && error.code === RxErrorKeys.WRONG_PASSWORD;

const getInitialisationError = (error: unknown): RxDBInitialisationError => {
  if (error instanceof RxError) {
    switch (error.code) {
      case RxErrorKeys.WRONG_PASSWORD:
      case RxErrorKeys.DB_ALREADY_EXISTS:
        return RxDBInitialisationError.RECOVERABLE;
      default:
        return RxDBInitialisationError.FATAL;
    }
  }
  return RxDBInitialisationError.FATAL;
};
